import { OBJ } from '../constants';
import { newId } from './objects';

/**
 * Table geometry — pure, no font measuring (that lives in drawObjects so the two
 * don't import each other). A table is ONE object:
 *
 *   { type:'table', x, y, w, h, rows, cols,
 *     cells: string[rows][cols],      // cell text
 *     rowHeights: number[rows],       // points; row auto-grows to its tallest cell
 *     borderColor, borderWidth, padX, padY,
 *     fontSize, fontFamily, fontStack, lineHeight, color, align }  // shared cell style
 *
 * Columns are equal width (w / cols) unless the optional fields below say otherwise.
 * `h` is always sum(rowHeights).
 *
 * Optional, all backwards compatible (absent → the table is the plain grid it always was):
 *     colFracs: number[cols]          // column widths as fractions of `w` (they follow a resize)
 *     merges: {r,c,rs,cs}[]           // merged cells: anchor (r,c) spanning rs rows × cs columns;
 *                                     // the anchor holds the text, the covered cells stay empty
 *     cellStyles: { 'r,c': { bold, italic, align, fontSize, valign } }   // per-cell overrides
 *     rowMin: number[rows]            // minimum row heights (points), e.g. a tall boxed block
 *     valign: 'top' | 'middle'        // default vertical placement of cell text
 *     borderWidth: 0                  // no rules drawn (a layout table)
 */

export const TABLE_MIN_COL = 24;      // points — a column never narrower than this
export const DEFAULT_PAD_X = 6;
export const DEFAULT_PAD_Y = 4;

/** Minimum height of one row for a given text style (one empty line + padding). */
export function minRowHeight(style) {
    return (style.fontSize || 12) * (style.lineHeight || 1.3) + (style.padY ?? DEFAULT_PAD_Y) * 2;
}

/** A fresh table. Rows start at the one-line minimum; recomputeTable grows them to text. */
export function makeTable(rect, rows, cols, settings = {}) {
    const style = {
        fontSize: settings.fontSize || 12,
        fontFamily: settings.fontFamily || 'Hind Siliguri',
        fontStack: settings.fontStack || null,
        lineHeight: settings.lineHeight || 1.3,
        color: settings.color || '#1C1815',
        align: settings.align || 'left',
        padX: settings.padX ?? DEFAULT_PAD_X,
        padY: settings.padY ?? DEFAULT_PAD_Y,
    };
    const r = Math.max(1, rows | 0);
    const c = Math.max(1, cols | 0);
    const minH = minRowHeight(style);
    const rowHeights = Array.from({ length: r }, () => minH);
    const cells = Array.from({ length: r }, () => Array.from({ length: c }, () => ''));
    return {
        id: newId(OBJ.TABLE),
        type: OBJ.TABLE,
        x: rect.x,
        y: rect.y,
        w: Math.max(c * TABLE_MIN_COL, rect.w),
        h: rowHeights.reduce((s, v) => s + v, 0),
        rotation: 0,
        opacity: 1,
        rows: r,
        cols: c,
        cells,
        rowHeights,
        borderColor: settings.stroke || '#1C1815',
        borderWidth: settings.strokeWidth || 1,
        ...style,
    };
}

/** Column widths in points: `colFracs` lets columns differ, otherwise they share `w` equally. */
export function colWidths(t) {
    const n = t.cols;
    const fr = Array.isArray(t.colFracs) && t.colFracs.length === n ? t.colFracs : null;
    if (!fr) return Array.from({ length: n }, () => t.w / n);
    const sum = fr.reduce((acc, v) => acc + v, 0) || 1;
    return fr.map((f) => (f / sum) * t.w);
}

/** Width of column `c` (equal share when the table has no `colFracs`). */
export const colWidth = (t, c = 0) => colWidths(t)[c] ?? t.w / t.cols;

/** x of column `c`'s left edge (page space). */
export function colLeft(t, c) {
    const w = colWidths(t);
    let x = t.x;
    for (let i = 0; i < c; i += 1) x += w[i] || 0;
    return x;
}

/** y of row `r`'s top edge (page space). */
export function rowTop(t, r) {
    let y = t.y;
    for (let i = 0; i < r; i += 1) y += t.rowHeights[i] || 0;
    return y;
}

/** The merge that covers cell (r, c), anchor included — or null for an ordinary cell. */
export function mergeOf(t, r, c) {
    for (const m of t.merges || []) {
        if (r >= m.r && r < m.r + m.rs && c >= m.c && c < m.c + m.cs) return m;
    }
    return null;
}

/** Whether (r, c) is hidden under another cell's merge (it holds no text and draws no rule). */
export function isCovered(t, r, c) {
    const m = mergeOf(t, r, c);
    return !!m && !(m.r === r && m.c === c);
}

/** The per-cell overrides of (r, c), or an empty object. */
export const cellStyleOf = (t, r, c) => t.cellStyles?.[`${r},${c}`] || {};

/** The page-space rect of one cell — the whole merged area when the cell is part of a merge. */
export function cellRect(t, r, c) {
    const m = mergeOf(t, r, c);
    const r0 = m ? m.r : r;
    const c0 = m ? m.c : c;
    const rs = m ? m.rs : 1;
    const cs = m ? m.cs : 1;
    const widths = colWidths(t);
    let w = 0;
    for (let i = c0; i < c0 + cs; i += 1) w += widths[i] || 0;
    let h = 0;
    for (let i = r0; i < r0 + rs; i += 1) h += t.rowHeights[i] || 0;
    return { x: colLeft(t, c0), y: rowTop(t, r0), w, h };
}

/** Which cell a page-space point falls in (a merged area answers with its anchor), or null outside. */
export function pointToCell(t, px, py) {
    if (px < t.x || px > t.x + t.w || py < t.y || py > t.y + t.h) return null;
    const widths = colWidths(t);
    let c = t.cols - 1;
    let x = t.x;
    for (let i = 0; i < t.cols; i += 1) {
        if (px <= x + (widths[i] || 0)) { c = i; break; }
        x += widths[i] || 0;
    }
    let r = t.rows - 1;
    let y = t.y;
    for (let i = 0; i < t.rows; i += 1) {
        const h = t.rowHeights[i] || 0;
        if (py <= y + h || i === t.rows - 1) { r = i; break; }
        y += h;
    }
    const m = mergeOf(t, r, c);
    return m ? { r: m.r, c: m.c } : { r, c };
}

/**
 * The cell after (dir 1) or before (dir -1) (r, c) in reading order — row by
 * row — skipping cells hidden under a merge. Null past either end. Tab and
 * Shift+Tab walk a table this way in Word and Google Docs.
 */
export function stepCell(t, r, c, dir) {
    for (let k = r * t.cols + c + dir; k >= 0 && k < t.rows * t.cols; k += dir) {
        const rr = Math.floor(k / t.cols);
        const cc = k % t.cols;
        if (!isCovered(t, rr, cc)) return { r: rr, c: cc };
    }
    return null;
}

/** The cell above (dy -1) or below (dy 1) the cell at (r, c) — the merge anchor when it lands in one. */
export function verticalCell(t, r, c, dy) {
    const m = mergeOf(t, r, c);
    const row = dy < 0 ? (m ? m.r : r) - 1 : (m ? m.r + m.rs : r + 1);
    if (row < 0 || row >= t.rows) return null;
    const hit = mergeOf(t, row, c);
    return hit ? { r: hit.r, c: hit.c } : { r: row, c };
}

const clone2d = (cells) => cells.map((row) => row.slice());

/** Shift per-cell style keys: `fn(r, c)` returns the new [r, c] or null to drop the entry. */
function remapStyles(styles, fn) {
    if (!styles) return styles;
    const out = {};
    for (const [key, value] of Object.entries(styles)) {
        const [r, c] = key.split(',').map(Number);
        const next = fn(r, c);
        if (next) out[`${next[0]},${next[1]}`] = value;
    }
    return out;
}

/** Insert a blank row at index `at` (default: after the last). Heights fixed up by caller. */
export function addRow(t, at = t.rows) {
    const cells = clone2d(t.cells);
    const row = Array.from({ length: t.cols }, () => '');
    cells.splice(at, 0, row);
    const rowHeights = t.rowHeights.slice();
    rowHeights.splice(at, 0, minRowHeight(t));
    const out = { ...t, rows: t.rows + 1, cells, rowHeights };
    if (t.merges?.length) {
        out.merges = t.merges.map((m) => {
            if (m.r >= at) return { ...m, r: m.r + 1 };
            if (m.r + m.rs > at) return { ...m, rs: m.rs + 1 }; // the new row falls inside this merge
            return m;
        });
    }
    if (t.cellStyles) out.cellStyles = remapStyles(t.cellStyles, (r, c) => [r >= at ? r + 1 : r, c]);
    if (t.rowMin) { out.rowMin = t.rowMin.slice(); out.rowMin.splice(at, 0, 0); }
    return out;
}

/** Remove row `at` (default: last). Never removes the final row. */
export function removeRow(t, at = t.rows - 1) {
    if (t.rows <= 1) return t;
    const cells = clone2d(t.cells); cells.splice(at, 1);
    const rowHeights = t.rowHeights.slice(); rowHeights.splice(at, 1);
    const out = { ...t, rows: t.rows - 1, cells, rowHeights };
    if (t.merges?.length) {
        out.merges = t.merges
            .map((m) => {
                if (at < m.r) return { ...m, r: m.r - 1 };
                if (at < m.r + m.rs) return { ...m, rs: m.rs - 1 };
                return m;
            })
            .filter((m) => m.rs >= 1 && (m.rs > 1 || m.cs > 1));
    }
    if (t.cellStyles) out.cellStyles = remapStyles(t.cellStyles, (r, c) => (r === at ? null : [r > at ? r - 1 : r, c]));
    if (t.rowMin) { out.rowMin = t.rowMin.slice(); out.rowMin.splice(at, 1); }
    return out;
}

/** Insert a blank column at index `at` (default: after the last). Width unchanged (columns re-share). */
export function addCol(t, at = t.cols) {
    const cells = t.cells.map((row) => { const next = row.slice(); next.splice(at, 0, ''); return next; });
    const out = { ...t, cols: t.cols + 1, cells, w: Math.max(t.w, (t.cols + 1) * TABLE_MIN_COL) };
    if (t.colFracs?.length === t.cols) {
        const keep = t.cols / (t.cols + 1);
        out.colFracs = t.colFracs.map((f) => f * keep);
        out.colFracs.splice(at, 0, 1 / (t.cols + 1));
    }
    if (t.merges?.length) {
        out.merges = t.merges.map((m) => {
            if (m.c >= at) return { ...m, c: m.c + 1 };
            if (m.c + m.cs > at) return { ...m, cs: m.cs + 1 };
            return m;
        });
    }
    if (t.cellStyles) out.cellStyles = remapStyles(t.cellStyles, (r, c) => [r, c >= at ? c + 1 : c]);
    return out;
}

/** Remove column `at` (default: last). Never removes the final column. */
export function removeCol(t, at = t.cols - 1) {
    if (t.cols <= 1) return t;
    const cells = t.cells.map((row) => { const next = row.slice(); next.splice(at, 1); return next; });
    const out = { ...t, cols: t.cols - 1, cells };
    if (t.colFracs?.length === t.cols) {
        out.colFracs = t.colFracs.slice();
        out.colFracs.splice(at, 1);
    }
    if (t.merges?.length) {
        out.merges = t.merges
            .map((m) => {
                if (at < m.c) return { ...m, c: m.c - 1 };
                if (at < m.c + m.cs) return { ...m, cs: m.cs - 1 };
                return m;
            })
            .filter((m) => m.cs >= 1 && (m.rs > 1 || m.cs > 1));
    }
    if (t.cellStyles) out.cellStyles = remapStyles(t.cellStyles, (r, c) => (c === at ? null : [r, c > at ? c - 1 : c]));
    return out;
}
